import React, { useEffect, useState } from "react";
import { useTable, useSortBy, Column } from "react-table";
import { useDispatch, useSelector } from "react-redux";
import { RootState, actions } from "../../state/reducer";
import { EventData, EventFullData } from "../../types";
import { TableBurgerIcon } from "../../icons/tableBurger";
import Paginator from "../paginator";
import { CellSortIcon } from "../../icons/sort";
import ShortManInfo from "../ui/shortItem";
import { eventBackend } from "../../services";
import {
  defPageSize,
  statusNamesArr,
} from "../../constants/events";
import { formatDateTimeStandard, generateRandomString, getEventColorClass, isEventOutdated } from "../../utils";
import { closeModal, openModal } from "../../state/modalSlice";
import { toast } from "sonner";
import UpdateEventModal from "../../modals/event/updteEventStatus";

interface EventTableProps {
  columns: Column<EventData>[];
  data: EventData[];
}

const tableGridTemplate = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

const EventsTable: React.FC<EventTableProps> = ({ columns, data }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<EventTableProps>({ columns, data }, useSortBy);

  return (
    <div className="listTable" {...getTableProps()} style={{ display: "grid", gap: "10px" }}>
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{ cursor: column.canSort ? "pointer" : "default" }}
            >
              {column.render("Header")}
              {column.Header !== "" && typeof column.Header === "string" ? (
                <CellSortIcon
                  width={8}
                  height={5}
                  classNames={
                    column.isSortedDesc
                      ? ["sortedDesc", "grayFilled"]
                      : ["grayFilled"]
                  }
                />
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any) => (
                <div {...cell.getCellProps()}>{cell.render("Cell")}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EventsListTable = (props: { search: string }) => {
  const [totalCount, setTotalCount] = useState(0);
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const list = State.eventData;
  const data = list
    /* .filter((item) => {
      const searchRow = `${item.customer.name} ${item.date_start}`;
      return searchRow.indexOf(props.search) > -1;
    })
    .map((item) => ({
      ...item,
    })); */
  const eventTypeNames = State.eventTypes;

  useEffect(() => {
    eventBackend
      .getAllEventsCount(State.eventListRequestParams)
      .then((res) => {
        setTotalCount(res.count);
      })
      .catch((e) => {
        console.log("Error:", e);
      });
  }, [data]);

  const columns: Column<EventData>[] = [
    {
      Header: "",
      accessor: "type_id",
      canSort: true,
      Cell: ({ row }: any) => (
        <a href={`/events/detail/${row.original.id}`}>
          {eventTypeNames.find(
            (item) => item.id === Number(row.original.type_id)
          )?.name || row.original.type_id}
        </a>
      ),
    },
    {
      Header: "Клиент",
      accessor: "customer",
      canSort: true,
      Cell: ({ row }: any) => (
        <a href={`/client/${row.original.customer?.id}`}>
          <ShortManInfo
            name={row.original.customer?.phone}
            phone={row.original.customer?.first_name}
          />
        </a>
      ),
    },
    {
      Header: "Создано",
      accessor: "date",
      canSort: true,
      Cell: ({ row }: any) => (
        <div className="userRoleTableText">
          {row.original.date_start && formatDateTimeStandard(row.original.date_start)}
        </div>
      ),
    },
    {
      Header: "Дата выполнения",
      accessor: "date_end",
      canSort: true,
      Cell: ({ row }: any) => {
        const colorClass = row.original.date_end ? getEventColorClass(row.original) : "gray"
        return (
          <div className={`userRoleTableText ${colorClass}`}>
            {row.original.date_end? formatDateTimeStandard(row.original.date_end) : "Не указана"}
          </div>
        );
      },
    },
    {
      Header: "Инициатор",
      accessor: "user_author",
      canSort: true,
      Cell: ({ row }: any) => (
        <ShortManInfo
          photo={row.original.author.photoUrl}
          phone={row.original.author.phone}
          name={`${row.original.author.last_name} ${row.original.author.first_name}`}
        />
      ),
    },
    {
      Header: "Статус",
      accessor: "status_id",
      canSort: true,
      Cell: ({ row }: any) => {
        const isOutdated =
          isEventOutdated(row.original.date_end) &&
          Number(row.original.status_id) === 1;

        const statusName = isOutdated
          ? statusNamesArr[0]
          : statusNamesArr[row.original.status_id];

        const colorClass = getEventColorClass(row.original)

        return (
          <div className={`userRoleTableText ${colorClass}`}>
            {statusName}
          </div>
        );
      },
    },
    {
      Header: "Ответственный",
      accessor: "user_responsible",
      canSort: true,
      Cell: ({ row }: any) => (
        <ShortManInfo
          photo={
            row.original.responsible?.photoUrl || row.original.author.photoUrl
          }
          phone={row.original.responsible?.phone || row.original.author.phone}
          name={
            row.original.responsible
              ? `${row.original.responsible.last_name} ${row.original.responsible.first_name}`
              : `${row.original.author.last_name} ${row.original.author.first_name}`
          }
        />
      ),
    },
    {
      Header: "Действие",
      accessor: "action",
      canSort: false,
      Cell: ({ row }: any) => {
        let pending = false;
        const handleOpenStatusModal = () => {
          dispatch(
            openModal({
              component: UpdateEventModal,
              context: {
                event_id: row.original.id,
                onSave: () => {
                  dispatch(actions.selectItem(generateRandomString(4)));
                },
                closeModal: () => dispatch(closeModal()), // Закрыть модальное окно через Redux
              },
            })
          );
        };

        return (
          <button
            className="filterButton tableActionButton"
            type="button"
            onClick={handleOpenStatusModal}
          >
            <span>Выполнить</span>
          </button>
        );
      },
    },
  ];

  const handlePageSelect = (data: { page: number; perPage: number }) => {
    const offset = data.page * data.perPage;
    const eventFilters = State.eventListRequestParams;
    dispatch(
      actions.eventRequestParams({
        ...eventFilters,
        offset: Number(offset),
        limit: data.perPage,
      })
    );
  };

  return (
    <div className="tableContainer">
      <EventsTable data={data} columns={columns} />
      {totalCount > defPageSize && (
        <Paginator
          perPage={defPageSize}
          count={totalCount}
          onDataChange={handlePageSelect}
        />
      )}
    </div>
  );
};

export default EventsListTable;
